/* THE single owner of bottom-sheet history + system Back for the customer app.
   One global popstate/keydown listener, one layer stack. Every open sheet pushes exactly one
   namespaced history layer; every dismissal (Back, Escape, backdrop, swipe, X) consumes exactly one. */

import { useEffect, useRef } from "react";

let stack = [];
let suppress = 0;        // programmatic traversals still to be ignored
let pendingConsume = 0;  // layers queued for removal (flushed after the current commit)
let flushTimer = null;
let listening = false;
let seq = 0;

const isSheetState = (s) => !!(s && s.ponesiBottomSheet);
const here = () => window.location.pathname + window.location.search;

function pushLayer(id) {
  const base = window.history.state || {};
  window.history.pushState({ ...base, ponesiBottomSheet: true, sheetId: id }, "");
}

function consumeLayer() {
  suppress += 1;
  window.history.back();
}

// A close followed by an open in the same commit (sheet swap) must keep the layer count stable:
// the queued removal is cancelled by the new registration instead of eating its fresh layer.
function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    const n = pendingConsume;
    pendingConsume = 0;
    if (n <= 0) return;
    suppress += 1;
    window.history.go(-n);
  }, 0);
}

function onPop(e) {
  if (suppress > 0) { suppress -= 1; return; }
  const top = stack[stack.length - 1];
  if (!top) {
    // Stale marker (refresh, route change, unmounted sheet): skip it, never reopen anything.
    if (isSheetState(e.state)) consumeLayer();
    return;
  }
  if (top.getOpts().closeOnBack === false) {
    pushLayer(top.id);   // mandatory flow stays open; Back is swallowed, app never exits
    return;
  }
  stack = stack.slice(0, -1);
  top.close();
}

function onKey(e) {
  if (e.key !== "Escape") return;
  const top = stack[stack.length - 1];
  if (!top || top.getOpts().closeOnEscape === false) return;
  top.close();   // release() consumes the matching history layer
}

function ensureListeners() {
  if (listening) return;
  listening = true;
  window.addEventListener("popstate", onPop);
  window.addEventListener("keydown", onKey);
}

function register(entry) {
  ensureListeners();
  stack = [...stack, entry];
  if (pendingConsume > 0) { pendingConsume -= 1; return; }   // reuse the layer of the sheet it replaced
  pushLayer(entry.id);
}

function release(id) {
  const idx = stack.findIndex((s) => s.id === id);
  if (idx === -1) return;            // already consumed by Back — history is in sync
  const entry = stack[idx];
  stack = stack.filter((s) => s.id !== id);
  // Route changed underneath us: the layer is buried, onPop's stale branch will skip it.
  if (entry.path !== here()) return;
  pendingConsume += 1;
  scheduleFlush();
}

/** Registers an open sheet as the topmost Back/Escape owner. opts is read live. */
export function useSheetBack(open, onClose, opts = {}) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const optsRef = useRef(opts);
  optsRef.current = opts;

  useEffect(() => {
    if (!open) return undefined;
    const id = `ponesi-sheet-${++seq}`;
    register({
      id,
      path: here(),
      close: () => closeRef.current && closeRef.current(),
      getOpts: () => optsRef.current,
    });
    return () => release(id);
  }, [open]);
}

export const __sheetStackSize = () => stack.length;
